class ProductBackgroundWrapper extends HTMLElement {
  constructor() {
    super();
    this._scrollTrigger = null;
    this._isInitialized = false;
    this._observer = null;
  }

  connectedCallback() {
    // Use intersection observer to ensure element is visible before init
    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this._isInitialized) {
            // Add additional delay to ensure all layout is complete
            setTimeout(() => this.initializeAnimation(), 300);
          }
        });
      },
      {
        rootMargin: "100px 0px", // Start observing before element comes into view
      }
    );

    this._observer.observe(this);
  }

  initializeAnimation() {
    if (this._isInitialized || !this.isConnected) return;

    if (!window.gsap || !window.ScrollTrigger) {
      console.error("GSAP and ScrollTrigger must be loaded first.");
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const bars = this.querySelectorAll(".pbi-bar");
    if (!bars.length) {
      console.warn("No .pbi-bar elements found in", this);
      return;
    }

    // Reset bars to initial state and force style recalculation
    bars.forEach((bar) => {
      bar.style.height = "500px";
      bar.style.transform = "none";
      // Force reflow
      bar.offsetHeight;
    });

    // Get fresh position calculations
    const rect = this.getBoundingClientRect();
    const scrollY = window.pageYOffset;
    const elementTop = rect.top + scrollY;

    // Create completely isolated ScrollTrigger
    this._scrollTrigger = ScrollTrigger.create({
      trigger: this,
      start: () => `top 200%`,
      end: () => `top+=100px`,
      scrub: true,
      invalidateOnRefresh: true,
      fastScrollEnd: true,
      preventOverlaps: true,

      onUpdate: (self) => {
        const progress = self.progress;

        bars.forEach((bar, index) => {
          const staggerDelay = index * 0.1; // Increased from 0.04 to 0.1 for more pronounced stagger effect
          const adjustedProgress = Math.max(
            0,
            Math.min(1, progress - staggerDelay)
          );
          // FIXED: Changed from (100 - adjustedProgress * 500) to (500 - adjustedProgress * 500)
          // This makes bars animate from 500px to 0px instead of 500px to 100px
          const height = Math.max(0, 500 - adjustedProgress * 500);

          // Use direct style manipulation for better performance
          bar.style.height = `${height}px`;
        });
      },

      onRefresh: () => {
        // Reset on refresh
        bars.forEach((bar) => {
          bar.style.height = "500px";
          bar.style.transform = "none";
        });
      },
    });

    this._isInitialized = true;

    // Force immediate refresh for this specific instance
    requestAnimationFrame(() => {
      if (this._scrollTrigger) {
        this._scrollTrigger.refresh();
      }
    });
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    if (this._scrollTrigger) {
      this._scrollTrigger.kill();
      this._scrollTrigger = null;
    }

    this._isInitialized = false;
  }

  // Public method to force reinitialize
  reset() {
    this.cleanup();
    if (this.isConnected) {
      setTimeout(() => this.initializeAnimation(), 100);
    }
  }
}

// Enhanced registration
if (!customElements.get("product-background-wrapper")) {
  customElements.define("product-background-wrapper", ProductBackgroundWrapper);
}

// Debug utility - call this in console to see what's happening
window.debugProductWrappers = function () {
  const wrappers = document.querySelectorAll("product-background-wrapper");
  wrappers.forEach((wrapper, index) => {
    const rect = wrapper.getBoundingClientRect();
    console.log(`Wrapper ${index + 1}:`, {
      top: rect.top,
      bottom: rect.bottom,
      height: rect.height,
      isInitialized: wrapper._isInitialized,
      scrollTrigger: wrapper._scrollTrigger ? "exists" : "missing",
    });
  });
};

// Auto-reset all wrappers on window resize (debounced)
let resizeTimeout;
window.addEventListener("resize", () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    document
      .querySelectorAll("product-background-wrapper")
      .forEach((wrapper) => {
        if (wrapper.reset) wrapper.reset();
      });
  }, 250);
});